import axios from "axios";
import { NOTIFICATION_API_URL } from "../../utils/constants";

// Notification templates, sending and send history live in mhcet-notification-service, not the
// registration backend. The auth header is set globally by utils/setAuthToken; that service verifies
// the same back-office token and checks for the "Email" role.
const BASE = `${NOTIFICATION_API_URL}/backoffice`;
const data = (res) => res.data?.data;

// --- Notification template master (one template per event: SMS / WhatsApp / Email) ---

export const getSystemVariablesApi = () => axios.get(`${BASE}/notification-templates/system-variables`).then(data);

// active: true / false; channel: "sms" | "whatsapp" | "email" (only templates with that channel)
export const listTemplatesApi = ({ active, channel } = {}) =>
  axios.get(`${BASE}/notification-templates`, { params: { active, channel } }).then(data);

export const getTemplateApi = (templateid, version) =>
  axios.get(`${BASE}/notification-templates/${templateid}`, { params: version ? { version } : {} }).then(data);

// Returns the full reply: { message, data: { ...template, warnings } }
export const createTemplateApi = (template) => axios.post(`${BASE}/notification-templates`, template).then((res) => res.data);

export const updateTemplateApi = (templateid, template) =>
  axios.put(`${BASE}/notification-templates/${templateid}`, template).then((res) => res.data);

export const deleteTemplateApi = (templateid) => axios.delete(`${BASE}/notification-templates/${templateid}`).then((res) => res.data);

export const setTemplateActiveApi = (templateid, isactive) =>
  axios.patch(`${BASE}/notification-templates/${templateid}/status`, { isactive }).then(data);

// body: { templateid } or the template fields, plus candidateid, courseid, values, filevalues
export const previewTemplateApi = (body) => axios.post(`${BASE}/notification-templates/preview`, body).then((res) => res.data);

// body: { channel, to, candidateid?, courseid?, values?, filevalues? }
export const testTemplateApi = (templateid, body) =>
  axios.post(`${BASE}/notification-templates/${templateid}/test`, body).then((res) => res.data);

// --- Send by upload (SMS / WhatsApp / Email) ----------------------------

const uploadForm = ({ templateid, channels, file, courseid, values }) => {
  const form = new FormData();
  form.append("templateid", String(templateid));
  form.append("channels", JSON.stringify(channels));
  form.append("file", file);
  if (courseid) form.append("courseid", String(courseid));
  form.append("values", JSON.stringify(values || {}));
  return form;
};

// Check File: per channel how many will receive, skipped rows and why, a sample. Sends nothing.
export const previewUploadApi = (input) => axios.post(`${BASE}/upload/preview`, uploadForm(input)).then((res) => res.data);

// Replies 202 with one job per channel.
export const sendUploadApi = (input) => axios.post(`${BASE}/upload/send`, uploadForm(input)).then((res) => res.data);

// --- Send to a course (email / SMS / WhatsApp) --------------------------

export const listCoursesApi = () => axios.get(`${BASE}/communication/courses`).then(data);

export const listGroupsApi = () => axios.get(`${BASE}/communication/groups`).then(data);

export const countGroupApi = (courseid, group) =>
  axios.get(`${BASE}/communication/count`, { params: { courseid, group } }).then(data);

// body: { courseid, group, templateid, channels: ["sms", "whatsapp", "email"], values }
export const previewCommunicationApi = (body) => axios.post(`${BASE}/communication/preview`, body).then((res) => res.data);

export const sendCommunicationApi = (body) => axios.post(`${BASE}/communication/send`, body).then((res) => res.data);

// --- Send history (jobs of every channel) -------------------------------

export const listJobsApi = ({ channel, templateid, limit, offset }) =>
  axios.get(`${BASE}/notification-jobs`, { params: { channel, templateid, limit, offset } }).then(data);

export const getJobApi = (jobid) => axios.get(`${BASE}/notification-jobs/${jobid}`).then(data);

export const listJobRowsApi = (jobid, { status, limit, offset }) =>
  axios.get(`${BASE}/notification-jobs/${jobid}/recipients`, { params: { status, limit, offset } }).then(data);

// SendGrid delivery events of a job (latest event of each email).
export const listJobDeliveryApi = (jobid, { event, limit, offset }) =>
  axios.get(`${BASE}/email-logs`, { params: { jobid, event, latest: true, limit, offset } }).then((res) => res.data?.data);

// SMS delivery statuses of a job (latest status of each SMS), from the SMS gateway.
export const listJobSmsDeliveryApi = (jobid, { status, limit, offset }) =>
  axios.get(`${BASE}/sms-logs`, { params: { jobid, status, latest: true, limit, offset } }).then((res) => res.data?.data);

// WhatsApp statuses of a job (latest status of each message), as the WhatsApp provider reported them.
export const listJobWhatsappDeliveryApi = (jobid, { status, limit, offset }) =>
  axios.get(`${BASE}/whatsapp-logs`, { params: { jobid, status, latest: true, limit, offset } }).then((res) => res.data?.data);

// Ask the SMS gateway for pending delivery reports now (it also happens every few minutes by itself).
export const pollSmsDeliveryApi = () => axios.post(`${BASE}/sms-logs/poll`).then((res) => res.data);

// The message to show for a failed call.
export const errorMessage = (err, fallback = "Something went wrong") => {
  const status = err?.response?.status;
  if (status === 401) return "Your session has expired. Please log in again.";
  if (status === 403) return "Your role does not have access to this.";
  return err?.response?.data?.message || (err?.message === "Network Error" ? "Cannot reach the notification service" : fallback);
};
